import React, { useState } from 'react';
import { Upload, Button, Modal } from 'antd';
import { PlusOutlined, CloseOutlined } from '@ant-design/icons';
import useStyles from '../../styles/components/common/FileUploadList';
import useAppMessage from '../../hooks/useAppMessage';
import uploadService from '../../services/uploadService';
import MarkdownRenderer from './MarkdownRenderer';
import PDFViewer from './PDFViewer';
import FilePreviewModal from './FilePreviewModal';

import * as pdfjs from 'pdfjs-dist';

// 设置worker路径 - 使用CDN路径，避免CORS问题
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;


const FileUploadList = ({
  fileList = [],
  onChange,
  accept = 'image/*,video/*,.ppt,.pptx,.pdf',
  maxCount,
  uploadOptions,
  description = '上传',
  showUploadList = true,
  className = '',
}) => {
  const safeFileList = Array.isArray(fileList) ? fileList : [];
  const { styles } = useStyles();
  const { message, contextHolder } = useAppMessage();
  const [uploading, setUploading] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');
  const [previewType, setPreviewType] = useState('image'); // 'image' or 'pdf'

  // Modal body styles constants
  const modalBodyStyles = {
    pdf: { padding: 0, height: 'calc(80vh - 110px)' },
    markdown: { padding: 0, height: 'calc(80vh - 110px)' },
    video: { padding: 0, height: 'calc(80vh - 110px)' },
    default: { padding: 24 },
  };

  // 文件上传前校验
  const beforeUpload = async (file) => {
    // 先校验accept
    const acceptArr = (accept || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    let acceptMatched = false;
    for (let acc of acceptArr) {
      if (acc === '*') {
        acceptMatched = true;
        break;
      }
      if (acc.startsWith('.')) {
        if (file.name && file.name.toLowerCase().endsWith(acc.toLowerCase())) {
          acceptMatched = true;
          break;
        }
      } else if (acc.endsWith('/*')) {
        // 例如 image/*, video/*
        const typePrefix = acc.replace('/*', '');
        if (file.type && file.type.startsWith(typePrefix)) {
          acceptMatched = true;
          break;
        }
      } else {
        // 例如 image/png
        if (file.type === acc) {
          acceptMatched = true;
          break;
        }
      }
    }
    if (!acceptMatched) {
      window.message?.error('文件类型不被允许');
      return Upload.LIST_IGNORE;
    }

    // 类型判断
    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');
    const isPPT = /\.(ppt|pptx)$/i.test(file.name);
    const isDoc = /\.(doc|docx)$/i.test(file.name);
    const isMarkdown =
      /\.md$/i.test(file.name) || file.type === 'text/markdown' || file.content_type === 'text/markdown';
    const isPDF = /\.pdf$/i.test(file.name);

    // 图片格式验证 - 只允许 png, jpg, jpeg, webp
    if (isImage) {
      const allowedImageFormats = [
        'image/png',
        'image/jpeg',
        'image/jpg',
        'image/webp',
      ];
      const allowedImageExtensions = /\.(png|jpg|jpeg|webp)$/i;

      const isValidImageType = allowedImageFormats.includes(file.type);
      const isValidImageExtension = allowedImageExtensions.test(file.name);

      if (!isValidImageType && !isValidImageExtension) {
        window.message?.error('图片格式不支持，仅支持 PNG、JPG、JPEG、WEBP 格式');
        return Upload.LIST_IGNORE;
      }
    }

    // 总数限制
    if (maxCount && safeFileList.length >= maxCount) {
      window.message?.error(`最多上传${maxCount}个附件`);
      return Upload.LIST_IGNORE;
    }

    // 大小限制
    if (
      (isImage || isPPT || isDoc || isMarkdown || isPDF) &&
      file.size > 5 * 1024 * 1024
    ) {
      window.message?.error('图片大小不能超过5M');
      return Upload.LIST_IGNORE;
    }
    if (isVideo && file.size > 20 * 1024 * 1024) {
      window.message?.error('视频大小不能超过20M');
      return Upload.LIST_IGNORE;
    }

    // 视频时长限制
    if (isVideo) {
      const duration = await getVideoDuration(file);
      if (duration > 180) {
        window.message?.error('视频时长不能超过3分钟');
        return Upload.LIST_IGNORE;
      }
    }

    // PPT页数限制
    if (isPPT) {
      const pageCount = await getPPTPageCount(file);
      if (pageCount > 3) {
        window.message?.error('PPT不能超过3页');
        return Upload.LIST_IGNORE;
      }
    }

    // 文档页数限制
    if (isDoc) {
      const pageCount = await getDocPageCount(file);
      if (pageCount > 10) {
        window.message?.error('Word文档不能超过10页');
        return Upload.LIST_IGNORE;
      }
    }

    // PDF页数限制
    if (isPDF) {
      const pageCount = await getPDFPageCount(file);
      if (pageCount > 10) {
        window.message?.error('PDF不能超过10页');
        return Upload.LIST_IGNORE;
      }
    }

    // 类型限制
    const isAllowed =
      isImage || isVideo || isPPT || isDoc || isMarkdown || isPDF;
    if (!isAllowed) {
      window.message?.error('仅支持图片、PDF文件');
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  // 上传文件
  const handleUpload = async (file) => {
    setUploading(true);
    try {
      console.log('开始上传文件:', file.name, 'uploadOptions:', uploadOptions);
      const result = await uploadService.uploadFile(file, uploadOptions);
      console.log('上传服务返回结果:', result);
      
      // 详细调试日志
      console.log('=== 调试上传结果 ===');
      console.log('result:', result);
      console.log('result.url:', result?.url);
      console.log('result.file_url:', result?.file_url);
      console.log('result.fields?.file_url:', result?.fields?.file_url);
      console.log('URL验证条件:', !result || (!result.url && !result.file_url && !result.fields?.file_url));
      console.log('===================');
      
      // 验证上传结果
      if (!result || (!result.url && !result.file_url && !result.fields?.file_url)) {
        console.error('上传结果验证失败:', {
          hasResult: !!result,
          hasUrl: !!result?.url,
          hasFileUrl: !!result?.file_url,
          hasFieldsFileUrl: !!result?.fields?.file_url,
          result: result
        });
        throw new Error('上传结果缺少文件URL');
      }
      
      // 标准化content_type字段
      const normalizeContentType = (type) => {
        if (type === 'application/pdf') return 'pdf';
        if (type && type.startsWith('image/')) return 'image';
        if (type && type.startsWith('video/')) return 'video';
        if (type === 'text/markdown') return 'markdown';
        return type;
      };

      const newFile = {
        uid: file.uid,
        name: file.name,
        type: file.type,
        size: file.size,
        // 支持多种ID字段格式
        id: result.id || result.file_id,
        // 支持多种URL字段格式 - 新增对 fields.file_url 的支持
        url: result.url || result.file_url || result.fields?.file_url,
        // 标准化content_type字段
        content_type: normalizeContentType(result.content_type || result.mime_type || result.fields?.mime_type || file.type),
        status: 'done',
        // 保留额外的字段信息
        alt_text: result.alt_text || result.fields?.alt_text,
        format: result.format || result.fields?.format,
        inserted_at: result.inserted_at,
        updated_at: result.updated_at,
        author_id: result.author_id,
        // 保留 fields 中的其他信息
        file_size: result.fields?.file_size,
        height: result.fields?.height,
        width: result.fields?.width,
      };
      
      console.log('构建的文件对象:', newFile);
      
      // 只保留已上传成功的文件
      onChange && onChange([...safeFileList.filter((f) => f.url), newFile]);
      window.message?.success(`${file.name} 上传成功`);
    } catch (e) {
      console.error('文件上传失败:', e);
      // 提供更详细的错误信息
      const errorMessage = e.message || '上传失败，请重试';
      window.message?.error(`${file.name} 上传失败: ${errorMessage}`);
    } finally {
      setUploading(false);
    }
    return false;
  };

  // 删除文件
  const handleRemove = (file) => {
    onChange && onChange(safeFileList.filter((f) => f.uid !== file.uid));
  };

  // 预览大图或PDF
  const handlePreview = (file) => {
    const previewUrl = file.thumbUrl || file.url || '';
    console.log('Preview URL:', previewUrl, 'File:', file); // Debug log
    setPreviewImage(previewUrl);
    setPreviewTitle(file.name || '文件预览');

    // 判断文件类型设置预览类型
    if (file.type && file.type.startsWith('image/')) {
      setPreviewType('image');
    } else if (file.type && file.type.startsWith('video/')) {
      setPreviewType('video');
    } else if (/\.pdf$/i.test(file.name) || file.type === 'application/pdf') {
      setPreviewType('pdf');
    } else if (/\.md$/i.test(file.name) || file.type === 'text/markdown') {
      setPreviewType('markdown');
    } else {
      setPreviewType('image'); // 默认
    }

    setPreviewVisible(true);
  };

  // 文件预览渲染
  const renderFilePreview = (file) => {
    console.log('renderFilePreview', file);
    if (file.type && file.type.startsWith('image/')) {
      const previewUrl = file.thumbUrl || file.url;
      if (previewUrl) {
        return (
          <img
            src={previewUrl}
            alt={file.name}
            className={styles.imagePreview}
            onClick={() => handlePreview(file)}
          />
        );
      }
      // 没有url说明还没上传完
      return <span className={styles.uploadingText}>上传中</span>;
    }
    if (file.type && file.type.startsWith('video/')) {
      const url = file.url;
      if (url) {
        return (
          <div className={styles.videoPreviewContainer}>
            <video
              src={url}
              className={styles.videoPreview}
              preload="metadata"
              muted
              onClick={() => handlePreview(file)}
            />
            <div className={styles.videoPlayIcon}>
              <svg viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8">
                <path d="M8 5v14l11-7z"/>
              </svg>
            </div>
          </div>
        );
      }
      // 没有url说明还没上传完
      return <span className={styles.uploadingText}>上传中</span>;
    }
    if (/\.(ppt|pptx)$/i.test(file.name)) {
      return (
        <div
          className={`${styles.fileTypeIcon} ${styles.pptIcon}`}
          onClick={() => handlePreview(file)}
        >
          PPT
        </div>
      );
    }
    if (/\.(doc|docx)$/i.test(file.name)) {
      return (
        <div
          className={`${styles.fileTypeIcon} ${styles.wordIcon}`}
          onClick={() => handlePreview(file)}
        >
          Word
        </div>
      );
    }
    if (/\.(pdf)$/i.test(file.name)) {
      return (
        <div
          className={`${styles.fileTypeIcon} ${styles.pdfIcon}`}
          onClick={() => handlePreview(file)}
        >
          PDF
        </div>
      );
    }
    if (/\.(md)$/i.test(file.name)) {
      return (
        <div
          className={`${styles.fileTypeIcon} ${styles.markdownIcon}`}
          onClick={() => handlePreview(file)}
        >
          MD
        </div>
      );
    }
    return (
      <div
        className={styles.genericFileIcon}
        onClick={() => handlePreview(file)}
      >
        文件
      </div>
    );
  };

  // 辅助函数：获取视频时长
  function getVideoDuration(file) {
    return new Promise((resolve) => {
      const url = URL.createObjectURL(file);
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = function () {
        URL.revokeObjectURL(url);
        resolve(video.duration);
      };
      video.src = url;
    });
  }

  // 辅助函数：获取PPT页数（占位，返回1）
  async function getPPTPageCount(file) {
    // TODO: 可用pptxjs解析真实页数
    return 1;
  }

  // 辅助函数：获取文档页数
  async function getDocPageCount(file) {
    // 检查是否为PDF文件
    if (/\.pdf$/i.test(file.name)) {
      return await getPDFPageCount(file);
    }
    // Word文档暂时返回1页，待后续实现
    if (/\.(doc|docx)$/i.test(file.name)) {
      return 1;
    }
    return 1;
  }

  // 辅助函数：获取PDF页数
  async function getPDFPageCount(file) {
    try {


      // 将文件转换为ArrayBuffer
      const arrayBuffer = await file.arrayBuffer();

      // 加载PDF文档
      const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise;

      return pdf.numPages;
    } catch (error) {
      console.error('获取PDF页数失败:', error);
      // 如果解析失败，返回1页避免阻塞上传
      return 1;
    }
  }

  return (
    <div className={styles.uploadListWrapper}>
      {contextHolder}
      {safeFileList.map((file) => (
        <div key={file.uid || file.id} className={styles.uploadFileItem}>
          <div className={styles.uploadFileThumb}>
            {renderFilePreview(file)}
          </div>
          <div className={styles.uploadFileName}>{file.name}</div>
          <Button
            className={styles.uploadFileRemove}
            shape="circle"
            icon={<CloseOutlined />}
            size="small"
            onClick={() => handleRemove(file)}
          />
        </div>
      ))}
      {(!maxCount || safeFileList.length < maxCount) && (
        <Upload
          multiple
          showUploadList={false}
          accept={accept}
          beforeUpload={async (file) => {
            const valid = await beforeUpload(file);
            if (valid === true) {
              return handleUpload(file);
            }
            return valid;
          }}
          fileList={[]}
        >
          <Button
            className={styles.uploadAddButton}
            icon={<PlusOutlined />}
            loading={uploading}
          >
            {description}
          </Button>
        </Upload>
      )}
      <FilePreviewModal
        visible={previewVisible}
        onClose={() => setPreviewVisible(false)}
        file={{
          content_type: previewType === 'video' ? 'video/mp4' : 
                       previewType === 'pdf' ? 'application/pdf' : 
                       previewType === 'markdown' ? 'text/markdown' : 'image/jpeg',
          file_name: previewTitle,
          url: previewImage
        }}
      />
    </div>
  );
};

export default FileUploadList;
